// Utils
import Color from 'color';
import { parseColorStrToRGBA, getHoverColor } from 'color-utils';
import { getColorCSSVar, getImgCSSVar } from './getCSSVarUtil';
import { getAttributeData } from './brandingUtil';

// Constants
import BRANDING from '../constants/brandingConst';
import BRANDING_KEYS from '../constants/brandingKeysConst';
import BRANDING_CSS_VARS from '../constants/brandingCSSVarsConst';

const getUtilityNavTheme = (attributes) => {
    const {
        UTILITY_NAV_BG_COLOR,
        UTILITY_NAV_LINK_COLOR,
        GLOBAL_PRIMARY_COLOR,
    } = BRANDING;

    // Get BG Color
    const { value: bgColor } = getAttributeData(
        attributes,
        BRANDING_KEYS[UTILITY_NAV_BG_COLOR],
    );

    const CONST = bgColor ? UTILITY_NAV_BG_COLOR : GLOBAL_PRIMARY_COLOR;

    return {
        ...getColorCSSVar(
            attributes,
            CONST,
            BRANDING_CSS_VARS[UTILITY_NAV_BG_COLOR],
        ),
        ...getColorCSSVar(
            attributes,
            UTILITY_NAV_LINK_COLOR,
            BRANDING_CSS_VARS[UTILITY_NAV_LINK_COLOR],
        ),
    };
};

const getFeaturedNavTheme = (attributes) => {
    const {
        GLOBAL_LINK_COLOR,
        FEATURED_NAV_LINK_COLOR,
        FEATURED_NAV_FLAG_BG_COLOR,
        FEATURED_NAV_FLAG_TEXT_COLOR,
    } = BRANDING;

    // Get BG Color
    const { value: linkColor } = getAttributeData(
        attributes,
        BRANDING_KEYS[FEATURED_NAV_LINK_COLOR],
    );

    const LINK_CONST = linkColor ? FEATURED_NAV_LINK_COLOR : GLOBAL_LINK_COLOR;

    return {
        ...getColorCSSVar(
            attributes,
            LINK_CONST,
            BRANDING_CSS_VARS[FEATURED_NAV_LINK_COLOR],
        ),
        ...getColorCSSVar(
            attributes,
            FEATURED_NAV_FLAG_TEXT_COLOR,
            BRANDING_CSS_VARS[FEATURED_NAV_FLAG_TEXT_COLOR],
        ),
        ...getColorCSSVar(
            attributes,
            FEATURED_NAV_FLAG_BG_COLOR,
            BRANDING_CSS_VARS[FEATURED_NAV_FLAG_BG_COLOR],
        ),
    };
};

const getMainNavTheme = (attributes) => {
    const {
        GLOBAL_PRIMARY_COLOR,
        MAIN_NAV_BG_COLOR,
        MAIN_NAV_TEXT_COLOR,
        MAIN_NAV_BORDER_COLOR,
        MAIN_NAV_HOVER_BG_COLOR,
    } = BRANDING;

    // Get BG Color
    const { value: bgColor } = getAttributeData(
        attributes,
        BRANDING_KEYS[MAIN_NAV_BG_COLOR],
    );

    const CONST = bgColor ? MAIN_NAV_BG_COLOR : GLOBAL_PRIMARY_COLOR;

    const variables = {
        ...getColorCSSVar(
            attributes,
            MAIN_NAV_TEXT_COLOR,
            BRANDING_CSS_VARS[MAIN_NAV_TEXT_COLOR],
        ),
        ...getColorCSSVar(
            attributes,
            CONST,
            BRANDING_CSS_VARS[MAIN_NAV_BG_COLOR],
        ),
    };

    // Get Masthead BG Color
    const { value: mainNavBGColorStr } = getAttributeData(
        attributes,
        BRANDING_KEYS[CONST],
    );

    if (mainNavBGColorStr) {
        const mainNavHoverBGColorHex = getHoverColor(mainNavBGColorStr);
        const mainNavBGColorRGBA = parseColorStrToRGBA(mainNavBGColorStr);
        const mainNavBGColor = Color({
            r: mainNavBGColorRGBA.r,
            g: mainNavBGColorRGBA.g,
            b: mainNavBGColorRGBA.b,
        });
        const { h, s, l } = mainNavBGColor.hsl().object();
        const newLightness = l + 20 > 100 ? 100 : l + 20;
        const borderColor = Color({ h, s, l: newLightness });
        const { r, g, b } = borderColor.rgb().object();
        const borderColorStr = `rgba(${Math.round(r)}, ${Math.round(
            g,
        )}, ${Math.round(b)}, 0.6)`;
        // Set Border Color Variable
        variables[BRANDING_CSS_VARS[MAIN_NAV_BORDER_COLOR]] = borderColorStr;
        // Set Hover Color Variable
        variables[
            BRANDING_CSS_VARS[MAIN_NAV_HOVER_BG_COLOR]
        ] = mainNavHoverBGColorHex;
    }

    return variables;
};

const getMastheadTheme = (attributes) => {
    const {
        GLOBAL_FONT_COLOR,
        MASTHEAD_BG_COLOR_START,
        MASTHEAD_BG,
        MASTHEAD_BG_IMG,
        MASTHEAD_BG_IMG_FITS_TO_AREA,
        MASTHEAD_BG_IMG_SIZE,
        MASTHEAD_BG_IMG_POSITION,
        MASTHEAD_BG_IMG_REPEAT,
        MASTHEAD_TEXT_COLOR,
    } = BRANDING;

    // Get BG Color
    const { value: mastheadTextColor } = getAttributeData(
        attributes,
        BRANDING_KEYS[MASTHEAD_TEXT_COLOR],
    );

    const MASTHEAD_TEXT_CONST = mastheadTextColor
        ? MASTHEAD_TEXT_COLOR
        : GLOBAL_FONT_COLOR;

    const mastheadVars = {
        ...getColorCSSVar(
            attributes,
            MASTHEAD_TEXT_CONST,
            BRANDING_CSS_VARS[MASTHEAD_TEXT_COLOR],
        ),
        ...getColorCSSVar(
            attributes,
            MASTHEAD_BG_COLOR_START,
            BRANDING_CSS_VARS[MASTHEAD_BG_COLOR_START],
        ),
        ...getImgCSSVar(
            attributes,
            MASTHEAD_BG_IMG,
            BRANDING_CSS_VARS[MASTHEAD_BG_IMG],
        ),
    };

    // Get Masthead BG Image URL
    const { value: bgImgURL } = getAttributeData(
        attributes,
        BRANDING_KEYS[MASTHEAD_BG_IMG],
    );

    if (!bgImgURL) {
        // Set Background Variable to Background Color Variable
        mastheadVars[BRANDING_CSS_VARS[MASTHEAD_BG]] = `var(${
            BRANDING_CSS_VARS[MASTHEAD_BG_COLOR_START]
        })`;
    } else {
        // Does image fill background?
        const { value: fillsBG } = getAttributeData(
            attributes,
            BRANDING_KEYS[MASTHEAD_BG_IMG_FITS_TO_AREA],
        );

        if (fillsBG) {
            // Set Background Size Variable to 'cover'
            mastheadVars[BRANDING_CSS_VARS[MASTHEAD_BG_IMG_SIZE]] = 'cover';
            // Set Repeat Variable to 'no-repeat'
            mastheadVars[BRANDING_CSS_VARS[MASTHEAD_BG_IMG_REPEAT]] =
                'no-repeat';
            // Set Position to 'center'
            mastheadVars[BRANDING_CSS_VARS[MASTHEAD_BG_IMG_POSITION]] =
                'center';
        } else {
            // Get Background Repeat
            const { value: repeat } = getAttributeData(
                attributes,
                BRANDING_KEYS[MASTHEAD_BG_IMG_REPEAT],
            );
            // Set Background Repeat Variable
            mastheadVars[BRANDING_CSS_VARS[MASTHEAD_BG_IMG_REPEAT]] = repeat;

            // Get Background Position
            const { value: bgPosition } = getAttributeData(
                attributes,
                BRANDING_KEYS[MASTHEAD_BG_IMG_POSITION],
            );
            // Set Background Position Variable
            mastheadVars[
                BRANDING_CSS_VARS[MASTHEAD_BG_IMG_POSITION]
            ] = bgPosition;
        }

        // Set Background Variable to Background With Image Variable
        mastheadVars[BRANDING_CSS_VARS[MASTHEAD_BG]] =
            'var(--masthead-bg-with-img)';
    }

    return mastheadVars;
};

const mastheadThemeUtil = (attributes) => {
    const mastheadVars = {
        ...getMastheadTheme(attributes),
        ...getMainNavTheme(attributes),
        ...getUtilityNavTheme(attributes),
        ...getFeaturedNavTheme(attributes),
    };

    return mastheadVars;
};

export default mastheadThemeUtil;
